import React, { useState, useEffect } from 'react';
import './CheckoutStyles.css';
import CashfreePayment from './CashfreePayment';

const PaymentForm = ({ onPaymentMethodSelect, selectedPaymentMethod, onContinue, onBack, totalAmount, order, onPaymentFailed, codAvailable = true }) => {
  const [errors, setErrors] = useState({});
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Filter payment methods based on COD availability
  const allPaymentMethods = [
    { id: 'cashfree', name: 'Online Payment (Cards/UPI/Netbanking)', icon: '💳', description: 'Pay securely using Credit/Debit Card, UPI, Netbanking, or Wallets' },
    { id: 'cod', name: 'Cash on Delivery', icon: '💵', description: 'Pay when your order is delivered. Additional charges of ₹40 will be applied.' }
  ];

  // Only include COD if it's available for all products
  const [paymentMethods, setPaymentMethods] = useState(() => {
    return codAvailable 
      ? allPaymentMethods 
      : allPaymentMethods.filter(method => method.id !== 'cod');
  });

  // Update payment methods when codAvailable changes
  useEffect(() => {
    setPaymentMethods(
      codAvailable 
        ? allPaymentMethods 
        : allPaymentMethods.filter(method => method.id !== 'cod')
    );
    
    // If COD was selected but is no longer available, clear the selection
    if (!codAvailable && selectedPaymentMethod?.id === 'cod') {
      onPaymentMethodSelect(null);
    }
  }, [codAvailable, selectedPaymentMethod, onPaymentMethodSelect]);

  const handlePaymentMethodSelect = (method) => {
    onPaymentMethodSelect(method);
  };

  const handleContinue = () => {
    // Clear any previous errors
    setPaymentError('');
    
    // For COD, no validation needed
    if (selectedPaymentMethod && selectedPaymentMethod.id === 'cod') {
      onContinue();
      return;
    }
    
    // For Cashfree, the payment is handled in the CashfreePayment component
    // No need to do anything here as the CashfreePayment component will call onContinue
  };

  // Function to handle successful payment
  const handlePaymentSuccess = (data) => {
    console.log('Payment success handler called with data:', data);
    
    // Set payment success state
    setPaymentSuccess(true);
    setPaymentData(data);
    setPaymentStatus('success');
    setPaymentProcessing(false);
    
    // Show success message for 2 seconds before continuing
    // Pass the payment data to the parent component to process the order
    // and move to the confirmation step
    setTimeout(() => {
      console.log('Calling onContinue with payment data after timeout');
      onContinue(data);
    }, 2000);
  };

  return (
    <div className="payment-form-container">
      <h2>Payment Method</h2>
      
      {!codAvailable && (
        <div className="cod-unavailable-notice" style={{ 
          padding: '12px', 
          marginBottom: '20px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: '5px',
          color: '#856404'
        }}>
          <strong>⚠️ Cash on Delivery Not Available:</strong> Some items in your cart do not support Cash on Delivery. Please use online payment methods.
        </div>
      )}
      
      <div className="payment-methods">
        {paymentMethods.map((method) => (
          <div 
            key={method.id} 
            className={`payment-method-card ${selectedPaymentMethod && selectedPaymentMethod.id === method.id ? 'selected' : ''}`}
            onClick={() => handlePaymentMethodSelect(method)}
          >
            <span className="payment-icon">{method.icon}</span>
            <div className="payment-info">
              <span className="payment-name">{method.name}</span>
              <span className="payment-description">{method.description}</span>
            </div>
          </div>
        ))}
      </div>

      {selectedPaymentMethod && selectedPaymentMethod.id === 'cashfree' && (
        order ? (
          <CashfreePayment 
            totalAmount={totalAmount}
            order={order}
            selectedPaymentMethod={selectedPaymentMethod}
            onSuccess={(paymentData) => {
              // Handle successful payment
              console.log('Cashfree payment successful:', paymentData);
              // Show success message and then pass payment data to the parent component
              handlePaymentSuccess(paymentData);
            }}
            onError={(errorPayload) => {
              const paymentState = typeof errorPayload === 'object' && errorPayload?.paymentState
                ? errorPayload.paymentState
                : 'failed';
              const errorMessage = typeof errorPayload === 'object'
                ? errorPayload?.message
                : errorPayload;

              setPaymentError(
                errorMessage ||
                (paymentState === 'incomplete'
                  ? 'Payment not completed. Please proceed again.'
                  : 'Payment failed. Please try again.')
              );
              setPaymentStatus(paymentState);
              if (onPaymentFailed) onPaymentFailed({
                paymentState,
                message: errorMessage
              });
            }}
            onBack={() => onPaymentMethodSelect(null)}
          />
        ) : (
          <div className="payment-error-message">
            <p><strong>Error:</strong> Order not found. Please try again.</p>
          </div>
        )
      )}
      
      {selectedPaymentMethod && selectedPaymentMethod.id === 'cod' && (
        <div className="cod-info">
          <h3>Cash on Delivery</h3>
          <p>Pay when your order is delivered.</p>
          <p className="payment-note">Additional charges of ₹40 will be applied for Cash on Delivery.</p>
        </div>
      )}
      
      {/* Only show this Order Summary when NOT using Cashfree (Cashfree has its own) */}
      {selectedPaymentMethod?.id !== 'cashfree' && (
        <div className="order-summary-payment">
          <h3>Order Summary</h3>
          <div className="order-total">
            <span>Total Amount:</span>
            <span className="total-price">₹{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}
      
      {paymentProcessing && (
        <div className="payment-processing-overlay">
          <div className="payment-processing-content">
            <div className="spinner"></div>
            <p>Processing your payment...</p>
            <p className="payment-note">Please do not refresh or close this page.</p>
          </div>
        </div>
      )}

      {paymentSuccess && (
        <div className="payment-success-overlay">
          <div className="payment-success-content">
            <div className="success-icon">✓</div>
            <h3>Payment Successful!</h3>
            <p>Your payment has been processed successfully.</p>
            <p>Order ID: {paymentData?.orderId || 'N/A'}</p>
            <p>Payment ID: {paymentData?.paymentId || 'N/A'}</p>
            <p className="payment-note">Redirecting to order confirmation...</p>
          </div>
        </div>
      )}

      {paymentError && (
        <div className="payment-error-message">
          <p><strong>Payment Error:</strong> {paymentError}</p>
          <p>Please check your payment details and try again.</p>
        </div>
      )}

      {(!selectedPaymentMethod || selectedPaymentMethod.id !== 'cashfree') && (
        <div className="button-group">
          <button 
            className="back-button" 
            onClick={onBack}
            disabled={paymentProcessing || paymentSuccess}
          >
            Back to Address
          </button>
          <button 
            className="continue-button" 
            onClick={handleContinue}
            disabled={!selectedPaymentMethod || paymentProcessing || paymentSuccess}
          >
            {selectedPaymentMethod && selectedPaymentMethod.id === 'cod' ? 'Place Order' : 'Make Payment'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
